import { SandboxTariff } from '../../store';
import { SandboxOperationHandler } from '../types';
import { inputObject, page } from './utils';

const tariffView = (
  tariff: SandboxTariff,
  hostGroups: Array<{ id: number; title: string }>,
  index: number
): Record<string, unknown> => {
  const tariffHostGroups = hostGroups.filter((group) =>
    tariff.host_group_ids.includes(group.id)
  );

  return {
    ...tariff,
    show_in_shell: true,
    show_in_billing: true,
    is_active: true,
    lifetime: 60,
    sort: index,
    highlighted: index === 0,
    is_tracked: true,
    online_booking_enabled: true,
    ean: `sandbox-tariff-${String(tariff.id)}`,
    use_on_holidays: true,
    schedules: [{ start: 0, end: 86400 }],
    sell_schedules: [{ start: 0, end: 86400 }],
    price_list: [
      {
        host_groups: tariffHostGroups,
        cost_map: [
          { title: 'DEFAULT', value: Number(tariff.cost) },
          { title: 'USER_COST', value: Number(tariff.cost) },
        ],
      },
    ],
  };
};

export const shopResolvers: Record<string, SandboxOperationHandler> = {
  GetAvailableDiscounts: () => ({
    getAvailableDiscounts: [
      { id: 101, title: 'Скидка 5%', value: 5 },
      { id: 102, title: 'Скидка 10%', value: 10 },
      { id: 103, title: 'Скидка 15%', value: 15 },
    ],
  }),
  validatePromoCodeV2: (variables, store) => {
    const input = inputObject(variables);
    const code = String(input.code ?? '')
      .trim()
      .toUpperCase();
    if (code !== 'SANDBOX10') throw new Error('PROMO_CODE_NOT_FOUND');

    const items = Array.isArray(input.items)
      ? input.items.map((item) => {
          const promoItem = item as Record<string, unknown>;
          return {
            entity_id: Number(promoItem.entity_id),
            entity_type: String(promoItem.type),
          };
        })
      : [];

    return {
      validatePromoCode: {
        id: 901,
        author: { login: 'sandbox' },
        client: null,
        code,
        value: 10,
        amount: 0,
        entity: null,
        entity_id: null,
        created_at: store.getCurrentTimestamp(),
        items,
        promo_code: {
          id: 901,
          code,
          value: {
            value: 10,
            type: 'DISCOUNT',
            currency: { alias: 'RUB' },
          },
        },
      },
    };
  },
  goods: (variables, store) => {
    const input = inputObject(variables);
    const query = String(input.q ?? '').toLowerCase();
    const categoryId = Number(input.category_id ?? 0);
    const goods = store
      .getState()
      .goods.filter(
        (good) =>
          (!query || good.title.toLowerCase().includes(query)) &&
          (!categoryId || good.category.id === categoryId)
      )
      .map((good) => ({
        ...good,
        eans: [good.ean],
        wholesale_cost: Math.round(good.cost * 0.6),
        comment: '',
        show_in_shell: true,
        image: '',
        in_combo: store
          .getState()
          .combos.some((combo) =>
            combo.items.some(
              (item) =>
                item.entity_type === 'GOOD' && item.entity.id === good.id
            )
          ),
        low_stock_notification: { enabled: true, threshold: 5 },
        highlighted: false,
        category: { ...good.category, company_id: 1, show_in_shell: true },
      }));
    return { goods };
  },
  services: (variables, store) => {
    const query = String(inputObject(variables).q ?? '').toLowerCase();
    return {
      services: store
        .getState()
        .services.filter(
          (service) => !query || service.title.toLowerCase().includes(query)
        ),
    };
  },
  tariffs: (variables, store) => ({
    tariffs: page(
      store
        .getState()
        .tariffs.map((tariff, index) =>
          tariffView(tariff, store.getState().hostGroups, index)
        ),
      variables
    ),
  }),
  updateTariffSort: (variables, store) => {
    const input = Array.isArray(variables.input)
      ? (variables.input as Array<{ id: number; sort: number }>)
      : [];
    const sortById = new Map(input.map((item) => [Number(item.id), item.sort]));
    store.getState().tariffs.sort((left, right) => {
      const leftSort = sortById.get(left.id) ?? left.id;
      const rightSort = sortById.get(right.id) ?? right.id;
      return leftSort - rightSort;
    });
    return { updateTariffSort: true };
  },
  tariffGrid: (variables, store) => {
    const input = inputObject(variables);
    const hostId = Number(input.host_id ?? 0);
    const host = store.getState().hosts.find((item) => item.id === hostId);
    const busy =
      hostId &&
      store
        .getState()
        .sessions.some(
          (session) => session.host_id === hostId && session.status === 'ACTIVE'
        );
    const tariffs = hostId
      ? store
          .getState()
          .tariffs.filter(
            (tariff) => host && tariff.host_group_ids.includes(host.group_id)
          )
      : store.getState().tariffs;
    return { tariffGrid: busy ? [] : tariffs };
  },
  comboList: (variables, store) => {
    const input = inputObject(variables);
    const hostId = Number(input.host_id ?? 0);
    const host = store.getState().hosts.find((item) => item.id === hostId);
    const busy =
      hostId &&
      store
        .getState()
        .sessions.some(
          (session) => session.host_id === hostId && session.status === 'ACTIVE'
        );
    const combos = store.getState().combos.filter((combo) => {
      if (!hostId || !combo.tariff) return true;
      return Boolean(
        host && combo.tariff.host_group_ids.includes(host.group_id)
      );
    });
    return { comboList: busy ? [] : combos };
  },
  searchByEan: (variables, store) => {
    const ean = String(variables.ean ?? '');
    const good = store.getState().goods.find((item) => item.ean === ean);
    return {
      searchByEan: good ? { entity_id: good.id, entity_type: 'GOOD' } : null,
    };
  },
};
